import { useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ASSETS } from "../lib/assets";
import { BigButton } from "../design/BigButton";
import { Card } from "../design/BigButton";
import { Star } from "lucide-react";
import { speech } from "../audio/speechService";
import { sfx } from "../audio/sfx";
import { useSettings } from "../contexts/SettingsContext";
import { api } from "../lib/api";

export function LevelCompleteScreen() {
  const { profileId, levelId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const settings = useSettings();
  const state = location.state || {};
  const result = state.result || {};
  const level = state.level || {};
  const objects = state.objects || [];

  useEffect(() => {
    sfx.celebrate(settings.soundEffects === "on");
    speech.speak(
      `Amazing work! You found everything in this picture. You earned ${result.tokenBalance ? "many" : "some"} tokens.`,
      { speed: settings.narrationSpeed },
    );
    return () => speech.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stars = result.stars || 0;
  const credited = result.creditedAmounts || {};
  const wordList = objects.map((o) => o.learningObject?.displayName).filter(Boolean);

  return (
    <div className="wmleye-app level-complete" data-testid="level-complete">
      <img src={ASSETS.mascotOllie} alt="Ollie celebrating" className="level-complete__mascot" />
      <h1 className="level-complete__title" data-testid="level-complete-title">
        Amazing work! You found everything in this picture!
      </h1>
      <div className="level-complete__stars" data-testid="level-complete-stars">
        {[0, 1, 2].map((i) => (
          <Star
            key={i}
            size={64}
            fill={i < stars ? "#FFD447" : "transparent"}
            stroke={i < stars ? "#FFD447" : "#B7B0AA"}
            aria-hidden
          />
        ))}
      </div>
      <Card className="level-complete__breakdown">
        <h2>Bonuses earned</h2>
        <ul>
          {Object.entries(credited).map(([k, v]) => (
            <li key={k} data-testid={`bonus-${k}`}>
              {k.replace(/_/g, " ")}: +{v} tokens
            </li>
          ))}
          <li data-testid="bonus-max-streak">Max streak: {result.maxStreak || 0}</li>
          <li data-testid="bonus-fast-finds">Fast finds: {result.fastFinds || 0}</li>
          {result.perfectClear && (
            <li data-testid="bonus-perfect-clear" className="level-complete__perfect">
              ✨ Perfect Clear!
            </li>
          )}
        </ul>
        {wordList.length > 0 && (
          <>
            <h3>New words</h3>
            <p data-testid="bonus-wordlist">{wordList.join(", ")}</p>
          </>
        )}
      </Card>
      <div className="level-complete__actions">
        <BigButton
          variant="ghost"
          onClick={async () => {
            // Reset progress so Replay is truly a fresh run — earned stars
            // and past bonuses stay intact (they live on separate fields).
            try {
              await api.post("/gameplay/reset-level-progress", {
                childProfileId: profileId,
                levelId,
              });
            } catch (_) { /* keep UX unblocked if reset fails */ }
            navigate(`/child/${profileId}/play/${levelId}`, { replace: true });
          }}
          data-testid="level-complete-replay"
        >
          Replay
        </BigButton>
        <BigButton
          onClick={() => navigate(`/child/${profileId}/worlds`)}
          data-testid="level-complete-map"
        >
          Return to Map
        </BigButton>
        <BigButton
          variant="sun"
          onClick={() => navigate(`/child/${profileId}/worlds`)}
          data-testid="level-complete-next"
        >
          Next Level
        </BigButton>
      </div>
    </div>
  );
}
